import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Users, Send } from 'lucide-react';
import { listLiveVisitors, sendOutreach, type LiveVisitor } from '../api/liveVisitors';
import { getApiClient } from '../api/factory';
import { useT, type TFn } from '../i18n';
import { PageContainer, PageHeader } from '../components/Page';
import { EmptyState } from '../components/EmptyState';
import { SkeletonList } from '../components/Skeleton';

// ANX-069 — «Кто сейчас на сайте». Снапшот живых визитёров, polling каждые 8с
// (realtime-push отложен — operator-WS пока per-conversation, см. ANX-071).
// «Написать первым» создаёт диалог и переходит в него.

const POLL_MS = 8000;

export function LiveVisitorsPage() {
  const t = useT();
  const { data, isLoading } = useQuery({
    queryKey: ['live-visitors'] as const,
    queryFn: () => listLiveVisitors(getApiClient()),
    refetchInterval: POLL_MS,
  });

  const visitors = data?.visitors ?? [];
  const counts = data?.counts;

  return (
    <PageContainer width="narrow">
      <PageHeader title={t('liveVisitors.title')} />
      <p className="mb-3 text-xs text-slate-500">{t('liveVisitors.hint')}</p>

      {/* ANX-103 — счётчики активности: «сейчас» (живые) + окна. */}
      {counts && (
        <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <StatCard label={t('liveVisitors.counts.now')} value={counts.now} highlight />
          <StatCard label={t('liveVisitors.counts.last5min')} value={counts.last5min} />
          <StatCard label={t('liveVisitors.counts.lastHour')} value={counts.lastHour} />
          <StatCard label={t('liveVisitors.counts.today')} value={counts.today} />
        </div>
      )}

      {isLoading && <SkeletonList rows={3} />}
      {!isLoading && visitors.length === 0 && (
        <EmptyState icon={Users} title={t('liveVisitors.empty')} />
      )}
      {!isLoading && visitors.length > 0 && (
        <ul className="flex flex-col gap-2">
          {visitors.map((v) => (
            <LiveVisitorRow key={v.visitorId} v={v} t={t} />
          ))}
        </ul>
      )}
    </PageContainer>
  );
}

function StatCard({ label, value, highlight }: { label: string; value: number; highlight?: boolean }) {
  return (
    <div
      className={
        'rounded-md border p-3 ' +
        (highlight ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-white')
      }
    >
      <div className={'text-2xl font-semibold ' + (highlight ? 'text-emerald-700' : 'text-slate-900')}>
        {value}
      </div>
      <div className="mt-0.5 text-xs text-slate-500">{label}</div>
    </div>
  );
}

function LiveVisitorRow({ v, t }: { v: LiveVisitor; t: TFn }) {
  const navigate = useNavigate();
  const [composing, setComposing] = useState(false);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const label = v.name || v.email || t('liveVisitors.anonymous');

  function openConversation(id: string) {
    void navigate({ to: '/conversations/$id', params: { id } });
  }

  async function submit() {
    if (body.trim() === '') return;
    setSending(true);
    setError(null);
    try {
      const { conversationId } = await sendOutreach(getApiClient(), v.visitorId, body.trim());
      openConversation(conversationId);
    } catch {
      setError(t('liveVisitors.outreachError'));
      setSending(false);
    }
  }

  return (
    <li className="rounded-md border border-slate-200 bg-white p-3 shadow-card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-900">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" aria-hidden="true" />
            <span className="truncate">{label}</span>
          </div>
          {v.page && (
            <div className="mt-0.5 truncate text-xs text-slate-500" title={v.page}>
              {v.page}
            </div>
          )}
        </div>
        <div className="shrink-0">
          {v.conversationId ? (
            <button
              type="button"
              onClick={() => openConversation(v.conversationId as string)}
              className="rounded-md border border-slate-200 px-2.5 py-1 text-xs text-slate-700 hover:bg-slate-50"
            >
              {t('liveVisitors.openConversation')}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setComposing((c) => !c)}
              className="inline-flex items-center gap-1 rounded-md bg-slate-900 px-2.5 py-1 text-xs font-medium text-white hover:bg-slate-800"
            >
              <Send size={12} aria-hidden="true" />
              {t('liveVisitors.writeFirst')}
            </button>
          )}
        </div>
      </div>

      {composing && !v.conversationId && (
        <div className="mt-2 flex flex-col gap-2">
          <textarea
            rows={2}
            value={body}
            onChange={(e) => setBody(e.currentTarget.value)}
            placeholder={t('liveVisitors.outreachPlaceholder')}
            aria-label={t('liveVisitors.writeFirst')}
            className="rounded border border-slate-300 px-2 py-1 text-sm focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-200"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={sending || body.trim() === ''}
              onClick={() => void submit()}
              className="rounded-md bg-accent-600 px-3 py-1 text-xs font-medium text-white hover:bg-accent-700 disabled:opacity-50"
            >
              {sending ? t('liveVisitors.sending') : t('liveVisitors.send')}
            </button>
            {error && <span role="alert" className="text-xs text-red-600">{error}</span>}
          </div>
        </div>
      )}
    </li>
  );
}
